import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Ghost, Send, ChevronLeft, Lock, ShieldCheck, Mic, Play, Pause, FileSignature } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import GhostwireContracts from "@/components/cef/phoneWireContracts";

/* Ghostwire — Telegram parody. Encrypted threads with plugs and customers.
   The default Messages app is tappable by police; nothing said here can be read.
   NPCs answer with persona-flavoured replies and will push you here if you try
   to deal over SMS. Real plugs sometimes send voice instead of text. */

const fmtDur = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

/* Voice note bubble — waveform bars fill as the note plays. Live audio streams from
   the hub (TTS mp3 per persona); demo mode fakes playback so previews show the UI. */
function VoiceNote({ m, serverId, demo }) {
  const audioRef = useRef(null);
  const timer = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [prog, setProg] = useState(0); // 0..1
  const dur = m.duration_s || 3;
  const bars = useMemo(() => {
    const seed = (m.id || "voice").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    return Array.from({ length: 16 }, (_, i) => 5 + ((seed + i * 53) % 15));
  }, [m.id]);

  useEffect(() => () => clearInterval(timer.current), []);

  const stop = () => { clearInterval(timer.current); timer.current = null; setPlaying(false); setProg(0); };

  const toggle = () => {
    if (playing) {
      audioRef.current?.pause();
      clearInterval(timer.current); timer.current = null;
      setPlaying(false);
      return;
    }
    setPlaying(true);
    if (demo || !m.audio_id) { // fake playback for the preview floor
      const t0 = Date.now() - prog * dur * 1000;
      timer.current = setInterval(() => {
        const p = (Date.now() - t0) / (dur * 1000);
        if (p >= 1) { stop(); } else setProg(p);
      }, 100);
      return;
    }
    const el = audioRef.current;
    if (el) { el.currentTime = prog * dur; el.play().catch(() => stop()); }
  };

  return (
    <div data-testid={`voice-note-${String(m.id).slice(0, 6)}`} className="flex items-center gap-2 min-w-[170px]">
      {!demo && m.audio_id && (
        <audio ref={audioRef} preload="none" className="hidden"
          src={`${process.env.REACT_APP_BACKEND_URL}/api/nc/servers/${serverId}/leaf/wire/audio/${m.audio_id}`}
          onTimeUpdate={(e) => setProg(Math.min(1, e.currentTarget.currentTime / (e.currentTarget.duration || dur)))}
          onEnded={stop} onError={stop} />
      )}
      <button data-testid={`voice-play-${String(m.id).slice(0, 6)}`} onClick={toggle}
        className="w-8 h-8 rounded-full bg-sky-300 text-black flex items-center justify-center shrink-0 transition-transform active:scale-90">
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
      </button>
      <div className="flex items-end gap-[2px] h-6 flex-1">
        {bars.map((h, i) => (
          <span key={i} className={`w-[3px] rounded-full transition-colors duration-150 ${i / bars.length <= prog ? "bg-sky-300" : "bg-white/25"}`}
            style={{ height: `${h}px` }} />
        ))}
      </div>
      <span className="font-mono text-[8px] text-white/50 shrink-0">{fmtDur(prog > 0 ? Math.max(0, dur - prog * dur) : dur)}</span>
    </div>
  );
}

export const WIRE_DEMO = [
  { npc: "NPC-greenthumb", name: "GreenThumb Gaz", suburb: "Route 68", unread: 1,
    last: { text: "Meet: Route 68 — abandoned servo, window opens 22:15. Don't be late.", at: "2026-09-18T21:58:00" } },
  { npc: "NPC-gh0st", name: "GH0ST", suburb: "The Docks", unread: 0,
    last: { text: "wire only.", at: "2026-09-18T20:12:00" } },
];
export const WIRE_DEMO_THREAD = [
  { id: "w1", from: "me", text: "yo, you holding?", at: "2026-09-18T21:50:00" },
  { id: "w2", from: "npc", text: "On the board rn: Violet Static $15/g (24g); Whiteout $19/g (11g)", at: "2026-09-18T21:51:00" },
  { id: "w3", from: "me", text: "14g haze. where and when", at: "2026-09-18T21:55:00" },
  { id: "w4", from: "npc", text: "Meet: Route 68 — abandoned servo, window opens 22:15. Don't be late.", kind: "voice", duration_s: 5, at: "2026-09-18T21:58:00" },
];

export default function GhostwireApp({ demo, serverId, token, accent, sfx, initialThread }) {
  const [threads, setThreads] = useState(demo ? WIRE_DEMO : []);
  const [open, setOpen] = useState(null); // npc id
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [root, setRoot] = useState("chats"); // chats | jobs (contract board + drops)
  const endRef = useRef(null);

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/leaf/wire/chats`, { params: { token } })
      .then((r) => setThreads(r.data.threads)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  // cross-app jump: GreenPages "Message on Ghostwire" lands straight in the thread
  useEffect(() => {
    if (initialThread) openThread(initialThread);
  }, [initialThread]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openThread = (npcId) => {
    sfx?.keyTick();
    setOpen(npcId);
    if (demo) { setMsgs(WIRE_DEMO_THREAD); return; }
    api.get(`/nc/servers/${serverId}/leaf/wire/chats/${npcId}`, { params: { token } })
      .then((r) => setMsgs(r.data.messages)).catch(() => setMsgs([]));
    load(); // clears unread
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    sfx?.sendSwoosh();
    if (demo) {
      setMsgs((m) => [...m, { id: `d${Date.now()}`, from: "me", text, at: new Date().toISOString() }]);
      setTimeout(() => setMsgs((m) => [...m, { id: `d${Date.now()}r`, from: "npc", text: "Noted. Board's updated. (demo)", at: new Date().toISOString() }]), 900);
    } else {
      try {
        await api.post(`/nc/servers/${serverId}/leaf/wire/chats/${open}/send`, { text }, { params: { token } });
        const r = await api.get(`/nc/servers/${serverId}/leaf/wire/chats/${open}`, { params: { token } });
        setMsgs(r.data.messages);
        sfx?.receiveDing();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }
    setDraft("");
    setSending(false);
  };

  if (open) {
    const thread = threads.find((t) => t.npc === open);
    return (
      <div data-testid="wire-thread" className="flex flex-col min-h-full">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10">
          <button data-testid="wire-back" onClick={() => setOpen(null)} className="text-white/60 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
          <span className="w-8 h-8 rounded-full bg-sky-400/15 border border-sky-400/30 flex items-center justify-center"><Ghost className="w-4 h-4 text-sky-300" /></span>
          <div className="flex-1">
            <p className="font-sans font-semibold text-sm text-white">{thread?.name || open}</p>
            <p className="font-mono text-[7px] text-sky-300/80 uppercase tracking-[0.15em] flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> encrypted · no tap · no trace</p>
          </div>
        </div>
        <div className="flex-1 px-3 py-2 flex flex-col gap-1.5 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
          {msgs.map((m) => (
            <div key={m.id} className={`max-w-[80%] rounded-2xl px-3 py-2 ${m.from === "me" ? "self-end bg-sky-500/25 border border-sky-400/30" : "self-start bg-white/8 border border-white/10"}`}>
              {m.kind === "voice"
                ? <VoiceNote m={m} serverId={serverId} demo={demo} />
                : <p className="font-mono text-[10px] text-white leading-relaxed">{m.text}</p>}
              <p className="font-mono text-[6px] text-white/30 mt-0.5 text-right flex items-center justify-end gap-1">
                {m.kind === "voice" && <Mic className="w-2 h-2" />}{String(m.at).slice(11, 16)}
              </p>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="p-2.5 border-t border-white/10 flex gap-1.5">
          <input data-testid="wire-input" value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message — it's encrypted…"
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-3.5 py-2 font-mono text-[10px] text-white focus:outline-none focus:border-sky-400/50" />
          <button data-testid="wire-send" onClick={send} disabled={sending || !draft.trim()}
            className="w-9 h-9 rounded-full flex items-center justify-center text-black disabled:opacity-40" style={{ backgroundColor: "#38bdf8" }}>
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="wire-app" className="p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2 px-1 pb-1">
        <Ghost className="w-4 h-4 text-sky-300" />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">Ghostwire</p>
        <p className="ml-auto font-mono text-[7px] text-emerald-300/80 uppercase tracking-[0.1em] flex items-center gap-1"><ShieldCheck className="w-2.5 h-2.5" /> e2e on</p>
      </div>
      {/* root tabs: encrypted chats vs the contract board */}
      <div className="flex gap-1">
        <button data-testid="wire-root-chats" onClick={() => setRoot("chats")}
          className={`flex-1 py-1.5 rounded-full font-mono text-[8px] font-bold uppercase tracking-[0.18em] border transition-colors ${root === "chats" ? "text-white border-sky-400/50 bg-sky-400/10" : "text-white/40 border-white/10"}`}>
          Chats
        </button>
        <button data-testid="wire-root-jobs" onClick={() => setRoot("jobs")}
          className={`flex-1 py-1.5 rounded-full font-mono text-[8px] font-bold uppercase tracking-[0.18em] border transition-colors flex items-center justify-center gap-1 ${root === "jobs" ? "text-white border-sky-400/50 bg-sky-400/10" : "text-white/40 border-white/10"}`}>
          <FileSignature className="w-3 h-3" /> Contracts & Drops
        </button>
      </div>
      {root === "jobs" ? (
        <GhostwireContracts demo={demo} serverId={serverId} token={token} accent={accent} />
      ) : (
        <>
      <p className="font-mono text-[8px] text-white/40 leading-relaxed px-1">Plugs won't deal on SMS — the default Messages app is tappable. Everything here is sealed.</p>
      {threads.length === 0 && <p data-testid="wire-empty" className="font-mono text-[10px] text-white/40 text-center mt-10">No threads — order on GreenPages and the plug opens a channel</p>}
      {threads.map((t) => (
        <button key={t.npc} data-testid={`wire-chat-${t.npc.slice(0, 10)}`} onClick={() => openThread(t.npc)}
          className="w-full rounded-2xl bg-white/5 border border-white/10 p-3 flex items-center gap-3 hover:bg-white/10 transition-colors text-left">
          <span className="w-10 h-10 rounded-full bg-sky-400/15 border border-sky-400/30 flex items-center justify-center shrink-0"><Ghost className="w-4 h-4 text-sky-300" /></span>
          <div className="flex-1 min-w-0">
            <p className="font-sans font-semibold text-sm text-white">{t.name}</p>
            <p className="font-mono text-[8px] text-white/40 truncate flex items-center gap-1">
              {t.last?.kind === "voice" ? <><Mic className="w-2.5 h-2.5 text-sky-300 shrink-0" /> voice note · {fmtDur(t.last?.duration_s)}</> : t.last?.text}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="font-mono text-[7px] text-white/30">{String(t.last?.at || "").slice(11, 16)}</p>
            {t.unread > 0 && <span className="inline-block mt-1 font-mono text-[8px] font-bold text-black bg-sky-300 rounded-full px-1.5 py-0.5">{t.unread}</span>}
          </div>
        </button>
      ))}
        </>
      )}
    </div>
  );
}
